/**
 * PACKAGE of the Publish workspace (W2-09 publish-import; DESIGN.md §4.16): what players see in song select. Title,
 * level label + difficulty number, charter, composer, illustrator, the song-select preview range (with play and
 * Phira's load-time validation, PH-36, and a one-click fix), division radio + free tags, description and tip.
 * Every edit is one undoable step (Core.doc.patchMeta; typing in a field coalesces per field).
 *
 * Each field's wrapper carries its document path as `data-focus-field` ('meta.name', 'meta.tags'...), so
 * `publish.focusField` (Problems "Go" through `inspector.focusField`, e.g. ST-09 about empty details or a missing
 * division) and `publish.details` land on its control.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { Expr } from '../../core/expr/index';
import type { ExprProgram } from '../../core/expr/types';
import type { AssetId, ChartMeta } from '../../core/model/types';
import type { RecordPatch } from '../../core/ops/types';
import type { MessageRef } from '../../core/util/types';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import { NumberField } from '../../ui/NumberField';
import { Section } from '../../ui/Section';
import { Segmented } from '../../ui/Segmented';
import { TextArea } from '../../ui/TextArea';
import { TextField } from '../../ui/TextField';
import { Field } from '../../ui/Field';
import type { DocStore } from '../../state/types';
import type { Text } from './hooks';
import { useSession } from './hooks';
import { clampedPreview, previewProblem } from './preflight';
import { publishSession } from './prefs';
import { m } from './publish.messages';
import { isBytesMissing, useStoredMissing } from './storedFiles';
import s from './Publish.module.css';

/** Phira's division tags (one of them is appended by Phira's own editor). */
export const DIVISIONS = ['regular', 'visual', 'troll', 'plain'] as const;
export type Division = (typeof DIVISIONS)[number];

const DIVISION_TITLES = {
  regular: m.divRegular,
  visual: m.divVisual,
  troll: m.divTroll,
  plain: m.divPlain,
  none: m.divNone,
} as const;

/** Phira plays this many seconds when previewEnd is unset (song select). */
export const DEFAULT_PREVIEW_LENGTH = 15;

export type MetaField =
  'name' | 'level' | 'difficulty' | 'charter' | 'composer' | 'illustrator' | 'preview' | 'tags' | 'intro' | 'tip';

/** One undoable edit of the chart details (coalesced per field); false with a toast when refused. */
export function patchMeta(
  services: Pick<AppServices, 'ui'>,
  store: DocStore,
  field: MetaField,
  patch: RecordPatch<ChartMeta>,
  label: MessageRef = { key: m.txnEditField, params: { field } },
): boolean {
  const out = store.transact(
    {
      label,
      command: 'publish.details',
      coalesceKey: `publish.meta.${field}`,
    },
    (tx) => {
      Core.doc.patchMeta(tx, { patch });
    },
  );
  if (!out.ok) services.ui.toast(out.reason, { kind: 'warning' });
  return out.ok;
}

/** The clamp of an invalid song-select preview (PH-36), as its own history step. */
export const CLAMP_PREVIEW_LABEL: MessageRef = { key: m.txnClampPreview };

/** The division tag of a tag list (the last one wins, as Phira reads it), or null. */
export function divisionOf(tags: readonly string[]): Division | null {
  for (let i = tags.length - 1; i >= 0; i--) {
    const t = tags[i]?.toLowerCase();
    if (t !== undefined && (DIVISIONS as readonly string[]).includes(t)) return t as Division;
  }
  return null;
}

/** Tags without the division tags. */
export function freeTags(tags: readonly string[]): string[] {
  return tags.filter((t) => !(DIVISIONS as readonly string[]).includes(t.toLowerCase()));
}

/** The tag list with this division (appended last, as Phira's editor writes it) and the free tags kept in order. */
export function withDivision(tags: readonly string[], division: Division | null): string[] {
  const rest = freeTags(tags);
  return division === null ? rest : [...rest, division];
}

/** Tags typed in one go ("a, b c" -> ["a", "b c"]), trimmed, without empties and without duplicates. */
export function parseTags(input: string, existing: readonly string[]): string[] {
  const have = new Set(existing.map((t) => t.toLowerCase()));
  const out: string[] = [];
  for (const raw of input.split(/[,;\n]/)) {
    const t = raw.trim();
    const key = t.toLowerCase();
    if (t === '' || have.has(key) || (DIVISIONS as readonly string[]).includes(key)) continue;
    have.add(key);
    out.push(t);
  }
  return out;
}

/** A committed number field value (a typed expression is evaluated against the current value). */
export function committedNumber(
  v: { readonly kind: 'number'; readonly value: number } | { readonly kind: 'expr'; readonly program: ExprProgram },
  current: number,
): number | null {
  if (v.kind === 'number') return Number.isFinite(v.value) ? v.value : null;
  return Expr.apply(v.program, { v: current, i: 0, n: 1, u: 0 }, Expr.rng(1));
}

/** Seconds offset between chart time and music time (META offset + info.yml offset). */
export function musicOffsetSec(store: DocStore): number {
  return store.doc.timing.offsetMs / 1000 + store.doc.pkg.infoOffsetSec;
}

// ---------------------------------------------------------------------------------------------------------------
// Song-select preview playback
// ---------------------------------------------------------------------------------------------------------------

interface PreviewPlayer {
  readonly playing: boolean;
  readonly disabledReason: MessageRef | null;
  toggle(): void;
}

/** Plays the song-select preview range once (then the playhead goes back to where it was). */
export async function playPreviewRange(
  services: Pick<AppServices, 'audio'>,
  meta: Pick<ChartMeta, 'previewStart' | 'previewEnd'>,
  offsetSec: number,
): Promise<number> {
  const audio = services.audio;
  await audio.ensureContext();
  const end = meta.previewEnd ?? meta.previewStart + DEFAULT_PREVIEW_LENGTH;
  const length = Math.max(0.1, end - meta.previewStart);
  await audio.transport.play({ from: meta.previewStart - offsetSec });
  const rate = audio.transport.state.rate > 0 ? audio.transport.state.rate : 1;
  return (length / rate) * 1000;
}

function usePreviewPlayer(services: AppServices, store: DocStore): PreviewPlayer {
  const [playing, setPlaying] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restore = useRef<number | null>(null);
  const ours = useRef(false);
  const transport = services.audio.transport;

  const finish = useCallback(
    (stopTransport: boolean) => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      const back = restore.current;
      restore.current = null;
      ours.current = false;
      if (stopTransport && transport.state.playing) transport.stop();
      if (back !== null) transport.seek(back);
      setPlaying(false);
    },
    [transport],
  );

  useEffect(
    () =>
      transport.onChange((st) => {
        // Stopped elsewhere (Space, another panel): forget the preview.
        if (!st.playing && ours.current) finish(false);
      }),
    [transport, finish],
  );
  useEffect(
    () => () => {
      if (ours.current) finish(true);
    },
    [finish],
  );

  const disabledReason: MessageRef | null = services.audio.music === null ? { key: m.previewNoSong } : null;
  const toggle = (): void => {
    if (playing) {
      finish(true);
      return;
    }
    if (disabledReason !== null) return;
    restore.current = transport.chartTime();
    ours.current = true;
    setPlaying(true);
    playPreviewRange(services, store.doc.meta, musicOffsetSec(store)).then(
      (ms) => {
        if (!ours.current) return;
        timer.current = setTimeout(() => {
          finish(true);
        }, ms);
      },
      () => {
        finish(false);
      },
    );
  };
  return { playing, disabledReason, toggle };
}

// ---------------------------------------------------------------------------------------------------------------
// Illustration
// ---------------------------------------------------------------------------------------------------------------

/** What the illustration box shows: no picture set, loading, the picture, missing from storage, or unreadable. */
type PictureState =
  | { readonly kind: 'none' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly url: string }
  | { readonly kind: 'missing' }
  | { readonly kind: 'failed' };

/**
 * The picture's object URL. Keyed by the asset and its presence (UX-067: a picture chosen again after it went
 * missing has the same id; when the probe sees it back, the URL is made again).
 */
function usePicture(
  services: Pick<AppServices, 'persist'>,
  id: AssetId | null,
  mime: string,
  missing: boolean,
): PictureState {
  const [loaded, setLoaded] = useState<{ readonly key: string; readonly state: PictureState } | null>(null);
  const key = `${id ?? ''}|${String(missing)}`;
  useEffect(() => {
    if (id === null || missing || typeof URL.createObjectURL !== 'function') return undefined;
    let alive = true;
    let url: string | null = null;
    services.persist.getAsset(id).then(
      (bytes) => {
        if (!alive) return;
        url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mime }));
        setLoaded({ key, state: { kind: 'ok', url } });
      },
      (err: unknown) => {
        if (alive) setLoaded({ key, state: { kind: isBytesMissing(err) ? 'missing' : 'failed' } });
      },
    );
    return () => {
      alive = false;
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [services, id, mime, missing, key]);
  if (id === null) return { kind: 'none' };
  if (missing) return { kind: 'missing' };
  return loaded !== null && loaded.key === key ? loaded.state : { kind: 'loading' };
}

function Illustration(props: { readonly services: AppServices; readonly store: DocStore; readonly text: Text }) {
  const { services, store, text } = props;
  const id = store.doc.meta.illustration;
  const asset = id === null ? undefined : store.doc.assets.get(id);
  const usable = asset !== undefined && asset.missing !== true ? asset : undefined;
  const probe = useStoredMissing(services, usable === undefined ? [] : [usable.id]);
  const picture = usePicture(
    services,
    usable?.id ?? null,
    usable?.mime ?? 'image/png',
    usable !== undefined && probe.has(usable.id),
  );
  // Not set (or a placeholder of an import that lacked the file), missing from storage, or unreadable (UX-066).
  const lost = picture.kind === 'missing' || asset?.missing === true;
  const label = picture.kind === 'failed' ? m.illustrationFailed : lost ? m.illustrationMissing : m.noIllustration;
  return (
    <div className={s.thumb}>
      <div className={s.thumbFrame} data-state={picture.kind}>
        {picture.kind === 'ok' ? (
          <img src={picture.url} alt={text.t(m.illustrationAlt, { name: store.doc.meta.name })} />
        ) : picture.kind === 'loading' ? null : (
          <span role={lost || picture.kind === 'failed' ? 'alert' : undefined}>
            <Icon name={lost ? 'ImageOff' : 'Image'} size={24} />
            <br />
            {text.t(label)}
          </span>
        )}
      </div>
      <Button
        size="sm"
        variant={lost ? 'primary' : 'ghost'}
        icon="ImageUp"
        onClick={() => {
          if (id !== null) void services.registry.run('assets.replace', { asset: id }, 'button');
          else void services.registry.run('assets.add', { role: 'illustration' }, 'button');
        }}
      >
        {text.t(lost ? m.chooseIllustrationAgain : id === null ? m.chooseIllustration : m.changeIllustration)}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------------------------------------------

export interface PackageFormProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly text: Text;
  readonly songSec: number | null;
}

export function PackageForm(props: PackageFormProps) {
  const { services, store, text, songSec } = props;
  const meta = store.doc.meta;
  const player = usePreviewPlayer(services, store);
  const rootRef = useRef<HTMLDivElement>(null);
  useSession();

  // Focus requests (publish.details, publish.focusField, the preview fix).
  const request = publishSession.focusRequest;
  useEffect(() => {
    if (request === null) return;
    const box = rootRef.current?.querySelector<HTMLElement>(`[data-focus-field="${request.field}"]`);
    // The division (a radio group) focuses its checked choice, other fields their first control.
    const el =
      box?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]') ??
      box?.querySelector<HTMLElement>('input, textarea, [role="radio"], button');
    if (el !== null && el !== undefined) {
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
      el.focus();
      publishSession.clearFocusRequest(request.nonce);
    }
  }, [request]);

  const text1 = (
    field: 'name' | 'level' | 'charter' | 'composer' | 'illustrator',
    label: string,
    extra: { readonly help?: string } = {},
  ) => (
    <div data-field={field} data-focus-field={`meta.${field}`}>
      <TextField
        label={label}
        value={meta[field]}
        maxLength={200}
        onCommit={(v) => {
          patchMeta(services, store, field, { [field]: v.trim() });
        }}
      />
      {extra.help !== undefined && <span className={s.muted}>{extra.help}</span>}
    </div>
  );

  const problem = previewProblem(meta, songSec);
  const fix = clampedPreview(meta, songSec);
  const end = meta.previewEnd ?? meta.previewStart + DEFAULT_PREVIEW_LENGTH;
  const division = divisionOf(meta.tags);
  const tags = freeTags(meta.tags);

  return (
    <Section title={text.t(m.packageTitle)} level={2}>
      <div className={s.form} ref={rootRef} data-help-id="publish.package">
        <div className={s.formHeader}>
          <Illustration services={services} store={store} text={text} />
          <div className={s.headerFields}>
            {text1('name', text.t(m.fieldName))}
            <div className={s.row2}>
              {text1('level', text.t(m.fieldLevel), { help: text.t(m.fieldLevelHelp) })}
              <div data-field="difficulty" data-focus-field="meta.difficulty">
                <NumberField
                  label={text.t(m.fieldDifficulty)}
                  value={{ kind: 'value', value: meta.difficulty }}
                  min={0}
                  max={100}
                  step={0.1}
                  precision={2}
                  stacked
                  onCommit={(v) => {
                    const d = committedNumber(v, meta.difficulty);
                    if (d !== null) patchMeta(services, store, 'difficulty', { difficulty: Math.max(0, d) });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={s.row2}>
          {text1('charter', text.t(m.fieldCharter))}
          {text1('composer', text.t(m.fieldComposer))}
          {text1('illustrator', text.t(m.fieldIllustrator))}
        </div>

        <Field label={text.t(m.fieldPreview)} help={{ key: m.previewHelp }} stacked helpId="publish.preview">
          <div className={s.previewRow} data-field="preview">
            <div data-focus-field="meta.previewStart">
              <NumberField
                label={text.t(m.previewFrom)}
                value={{ kind: 'value', value: meta.previewStart }}
                unit="s"
                min={0}
                step={0.5}
                precision={3}
                stacked
                onCommit={(v) => {
                  const start = committedNumber(v, meta.previewStart);
                  if (start === null) return;
                  const s0 = Math.max(0, start);
                  // Keep the length when the start moves past the end.
                  const patch: RecordPatch<ChartMeta> =
                    meta.previewEnd !== null && meta.previewEnd <= s0
                      ? { previewStart: s0, previewEnd: s0 + (meta.previewEnd - meta.previewStart) }
                      : { previewStart: s0 };
                  patchMeta(services, store, 'preview', patch);
                }}
              />
            </div>
            <div data-focus-field="meta.previewEnd">
              <NumberField
                label={text.t(m.previewTo)}
                value={{ kind: 'value', value: end }}
                unit="s"
                min={0}
                step={0.5}
                precision={3}
                stacked
                onCommit={(v) => {
                  const e = committedNumber(v, end);
                  if (e !== null) patchMeta(services, store, 'preview', { previewEnd: e });
                }}
              />
            </div>
            <IconButton
              icon={player.playing ? 'Square' : 'Play'}
              label={{ key: player.playing ? m.stopPreview : m.playPreview }}
              pressed={player.playing}
              disabledReason={player.playing ? null : player.disabledReason}
              onClick={() => {
                player.toggle();
              }}
            />
          </div>
        </Field>
        {problem !== null && (
          <div className={s.problem} role="alert">
            <Icon name="OctagonX" size={16} />
            <span>
              {text.t(m.previewProblem, { problem, song: songSec === null ? 0 : Math.round(songSec * 100) / 100 })}
            </span>
            {fix !== null && (
              <Button
                size="sm"
                icon="Wrench"
                onClick={() => {
                  patchMeta(services, store, 'preview', fix, CLAMP_PREVIEW_LABEL);
                }}
              >
                {text.t(m.fixClampPreview)}
              </Button>
            )}
          </div>
        )}

        {/* Division first (its own label), then the free tags with their chips (UX-080: "Tags" had no content). */}
        <div data-field="tags" data-focus-field="meta.tags" className={s.fieldStack}>
          <Segmented<Division | 'none'>
            label={text.t(m.division)}
            value={division ?? 'none'}
            size="sm"
            options={(['regular', 'visual', 'troll', 'plain', 'none'] as const).map((d) => ({
              value: d,
              label: text.t(DIVISION_TITLES[d]),
            }))}
            onChange={(d) => {
              patchMeta(services, store, 'tags', { tags: withDivision(meta.tags, d === 'none' ? null : d) });
            }}
          />
          <span className={s.muted}>{text.t(m.divisionHelp)}</span>
        </div>
        <Field label={text.t(m.fieldTags)} stacked helpId="publish.tags">
          <div className={s.fieldStack}>
            {tags.length > 0 && (
              <ul className={s.chips} aria-label={text.t(m.tagList)}>
                {tags.map((tag) => (
                  <li key={tag} className={s.chip}>
                    {tag}
                    <IconButton
                      icon="X"
                      size="sm"
                      label={{ key: m.removeTag, params: { tag } }}
                      onClick={() => {
                        patchMeta(services, store, 'tags', { tags: meta.tags.filter((t) => t !== tag) });
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <TextField
              label={text.t(m.addTag)}
              value=""
              placeholder={{ key: m.addTagPlaceholder }}
              maxLength={200}
              onCommit={(v) => {
                const added = parseTags(v, meta.tags);
                if (added.length === 0) return;
                patchMeta(services, store, 'tags', {
                  tags: withDivision([...freeTags(meta.tags), ...added], division),
                });
              }}
            />
          </div>
        </Field>

        <div data-field="intro" data-focus-field="meta.intro">
          <TextArea
            label={text.t(m.fieldIntro)}
            value={meta.intro}
            rows={3}
            maxLength={2000}
            onCommit={(v) => {
              patchMeta(services, store, 'intro', { intro: v });
            }}
          />
        </div>
        <div data-field="tip" data-focus-field="meta.tip">
          <TextField
            label={text.t(m.fieldTip)}
            value={meta.tip ?? ''}
            placeholder={{ key: m.tipPlaceholder }}
            maxLength={500}
            onCommit={(v) => {
              const tip = v.trim();
              patchMeta(services, store, 'tip', { tip: tip === '' ? null : tip });
            }}
          />
        </div>
      </div>
    </Section>
  );
}
